import React, { useState, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Row, Col, Button } from 'reactstrap';
import { noop } from 'lodash';
import { VehicleEntities } from 'client/data/models/vehicle';
import { VehicleDropdowns } from 'site-modules/shared/components/vehicle-dropdowns/vehicle-dropdowns';
import {
  ALLOWED_PUB_STATES,
  APPRAISER_STEP_BUTTON_TRACKING,
  LOCATION_AND_STYLE,
  COLORS_AND_OPTIONS,
  CONTINUE_BUTTON_CLASSNAME,
} from 'site-modules/shared/constants/appraisal/appraisal';
import {
  MAKE_INPUT,
  MODEL_INPUT,
  YEAR_INPUT,
  STYLE_INPUT,
} from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { ZipField, THEME } from 'site-modules/shared/components/appraisal/zip-field/zip-field';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { VehicleVinModel, buildStylesBasicPathFromVin } from 'client/data/models/vehicle-vin';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';

import { useStepLoadingHandler, useLocationAndStyleStepStorage } from './location-and-style-step-hooks';
import { VEHICLE_DROPDOWNS_TRACKING } from './location-and-style-step-constants';
import './location-and-style-step.scss';

export const LocationAndStyleStepUI = ({
  isZipCodeValid,
  onZipCodeChange,
  onDropdownsSelect,
  updateStepsState,
  selectStyle,
  isExtendedReport,
  isMobile,
  zipCode,
  styleIds,
  estimatedVehicle,
  errorMap,
  changeStep,
  isLoadingColorsAndOptions,
  vin,
  checkVinMismatchError,
  vinStyles,
  displayTipForTruckType,
  isStepActive,
  params,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const isLoaded = useStepLoadingHandler(isExtendedReport);
  const storage = useLocationAndStyleStepStorage();

  const onSelect = useCallback(
    (make, model, year, style, isSetVehicleDataForExtendedReport) => {
      setSelectedVehicle({ make, model, year: year?.year, id: style?.id });
      onDropdownsSelect(make, model, year, style, isSetVehicleDataForExtendedReport, () => {
        if (vin) {
          checkVinMismatchError(vinStyles);
        }
      });
    },
    [onDropdownsSelect, vin, vinStyles, checkVinMismatchError]
  );

  const handleOnClick = useCallback(
    e => {
      const { currentTarget } = e;
      delayHeavyTask(() => {
        changeStep({ currentTarget });
      });
    },
    [changeStep]
  );

  const styleDropdownId = `style-sel-${useId()}`;
  const zipId = `zip-${useId()}`;
  const hasVehicleData = !!estimatedVehicle;
  const errorMake = errorMap.get(MAKE_INPUT);
  const errorModel = errorMap.get(MODEL_INPUT);
  const errorYear = errorMap.get(YEAR_INPUT);
  const errorStyle = errorMap.get(STYLE_INPUT);

  const vehicleDropdownRender = useCallback(
    props => (
      <Row>
        <Col xs={12} className="px-0">
          <VehicleDropdowns
            tag="div"
            showAllSubmodels={hasVehicleData}
            hasModel
            hasYear
            hasStyle
            vehicle={selectedVehicle || estimatedVehicle}
            selectStyle={selectStyle}
            hasOptionTruncate={false}
            inputClasses="form-control pe-2 w-100 mmy-select input-content"
            makeLabel="Make"
            modelLabel="Model"
            yearLabel="Year"
            styleLabel="Style"
            labelClassName="small mb-0_25"
            buttonGroupClassNames="d-none"
            errorMake={errorMake}
            errorModel={errorModel}
            errorYear={errorYear}
            errorStyle={errorStyle}
            pubStatesYears={ALLOWED_PUB_STATES}
            isStyleIdQueryParamEnabled
            isMobile={isMobile}
            updateStepsState={updateStepsState}
            disabled={!isLoaded}
            styleIds={styleIds}
            enableSpinner
            displayTipForTruckType={displayTipForTruckType}
            required={{
              make: true,
              model: true,
              year: true,
              style: true,
            }}
            hasLiveRegions
            tracking={VEHICLE_DROPDOWNS_TRACKING}
            storage={storage}
            params={params}
            {...props}
          />
        </Col>
      </Row>
    ),
    [
      displayTipForTruckType,
      errorMake,
      errorModel,
      errorStyle,
      errorYear,
      estimatedVehicle,
      hasVehicleData,
      isLoaded,
      isMobile,
      params,
      selectStyle,
      selectedVehicle,
      storage,
      styleIds,
      updateStepsState,
    ]
  );

  const renderExtendedLayout = useCallback(
    () =>
      isMobile ? (
        <>
          {vehicleDropdownRender({
            makeDropdownClassNames: 'custom-select-arrow has-right-padding mb-1 p-0 pe-md-0_75 pe-lg-1',
            modelDropdownClassNames: 'custom-select-arrow mb-1 p-0 ps-md-0_75 ps-lg-1',
            submodelDropdownClassNames: 'custom-select-arrow mb-1 p-0 ps-md-0_75 ps-lg-1',
            yearDropdownClassNames: 'custom-select-arrow has-right-padding mb-1 mb-md-0 p-0 pe-md-0_75 pe-lg-1',
            styleDropdownClassNames: 'custom-select-arrow mb-0 p-0 ps-lg-1 ps-md-0_75 pos-r',
            onDropdownsSelect: onSelect,
            showFindStyleLink: true,
            styleDropdownId,
          })}
          <Row>
            <Col
              xs={4}
              md={6}
              className={classNames('px-0 mt-md-3', {
                'mt-2': !isMobile,
                'mt-0_25': isMobile,
              })}
            >
              <ZipField
                id={zipId}
                theme={THEME.LOCATION_ICON}
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-0_75"
                errorMap={errorMap}
                disabled={!isLoaded}
                isMobile
              />
            </Col>
            <Col xs={7} md={6} lg={4} className="px-0 mt-md-3 ms-auto ps-md-0_75 mt-1_25">
              <Button
                type="submit"
                onClick={handleOnClick}
                data-index={COLORS_AND_OPTIONS}
                data-tracking-id={APPRAISER_STEP_BUTTON_TRACKING[LOCATION_AND_STYLE]}
                color="success"
                className={classNames(
                  'heading-5 text-white py-0_5 form-button text-transform-none pos-r d-block w-100',
                  CONTINUE_BUTTON_CLASSNAME
                )}
                disabled={!isLoaded || isLoadingColorsAndOptions}
              >
                {isLoadingColorsAndOptions && (
                  <span className="pe-0_5">
                    <Spinner size={14} thickness={1} color="white" />
                  </span>
                )}
                Continue
              </Button>
            </Col>
          </Row>
        </>
      ) : (
        <>
          {vehicleDropdownRender({
            errorMessageClassNames: 'mt-0_5',
            makeDropdownClassNames: 'has-right-padding mb-1 p-0 pe-md-0_75 pe-lg-1',
            modelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            submodelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            yearDropdownClassNames: 'has-right-padding mb-1 mb-md-0 p-0 pe-md-0_75 pe-lg-1',
            styleDropdownClassNames: 'mb-0 p-0 ps-lg-1 ps-md-0_75 pos-r style-dropdown-error-gutter',
            isStyledSelects: true,
            onDropdownsSelect: onSelect,
            showFindStyleLink: true,
            styleDropdownId,
          })}
          <Row>
            <Col xs={12} md={6} className="px-0 mt-1">
              <ZipField
                id={zipId}
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-1 mb-0 mmy-zip-code"
                errorMap={errorMap}
                disabled={!isLoaded}
              />
            </Col>
          </Row>
        </>
      ),
    [
      errorMap,
      handleOnClick,
      isLoaded,
      isLoadingColorsAndOptions,
      isMobile,
      isZipCodeValid,
      onSelect,
      onZipCodeChange,
      styleDropdownId,
      vehicleDropdownRender,
      zipCode,
      zipId,
    ]
  );

  if (isExtendedReport && !isStepActive) return null;

  return (
    <div className="location-and-style-step">
      {isExtendedReport ? (
        renderExtendedLayout()
      ) : (
        <>
          <Row>
            <Col xs={12} md={6} className="px-0">
              <ZipField
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-1"
                errorMap={errorMap}
                disabled={!isLoaded}
                zipWrapperClassName="mb-1"
              />
            </Col>
          </Row>
          {vehicleDropdownRender({
            makeDropdownClassNames: 'has-right-padding mb-1 p-0 pe-md-0_75 pe-lg-1',
            modelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            submodelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            yearDropdownClassNames: 'has-right-padding mb-1 mb-md-0 p-0 pe-md-0_75 pe-lg-1',
            styleDropdownClassNames: 'mb-0 p-0 ps-lg-1 ps-md-0_75 pos-r',
            isStyledSelects: true,
            onDropdownsSelect,
          })}
        </>
      )}
    </div>
  );
};

LocationAndStyleStepUI.propTypes = {
  isZipCodeValid: PropTypes.bool.isRequired,
  onZipCodeChange: PropTypes.func.isRequired,
  onDropdownsSelect: PropTypes.func.isRequired,
  updateStepsState: PropTypes.func,
  selectStyle: PropTypes.bool,
  isExtendedReport: PropTypes.bool,
  isMobile: PropTypes.bool,
  zipCode: PropTypes.string,
  styleIds: PropTypes.string,
  estimatedVehicle: VehicleEntities.StyleWithoutSubmodels,
  errorMap: PropTypes.objectOf(PropTypes.string),
  changeStep: PropTypes.func,
  isLoadingColorsAndOptions: PropTypes.bool,
  vehicle: PropTypes.shape({}),
  vin: PropTypes.string,
  checkVinMismatchError: PropTypes.func,
  vinStyles: PropTypes.arrayOf(PropTypes.shape({})),
  displayTipForTruckType: PropTypes.bool,
  isStepActive: PropTypes.bool,
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
    styleId: PropTypes.string,
  }),
};

LocationAndStyleStepUI.defaultProps = {
  updateStepsState: noop,
  selectStyle: false,
  isExtendedReport: false,
  isMobile: false,
  zipCode: '',
  styleIds: '',
  estimatedVehicle: null,
  errorMap: new Map(),
  changeStep: noop,
  isLoadingColorsAndOptions: false,
  vehicle: {},
  vin: '',
  checkVinMismatchError: noop,
  vinStyles: null,
  displayTipForTruckType: false,
  isStepActive: false,
  params: {},
};

export const stateToPropsConfig = {
  vinStyles: bindToPath(props => buildStylesBasicPathFromVin(props), VehicleVinModel),
};

export const LocationAndStyleStep = connectToModel(LocationAndStyleStepUI, stateToPropsConfig);
